import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * SEVESHOP v5:
 * - toate operatiile pe cos folosesc data-key din randul din DOM (nu indexul de linie copt
 *   server-side, care devine invalid dupa un fast-refresh — ex: nu puteai sterge al doilea produs).
 * - fast path fara sections/morph; bara, total, economii, bubble actualizate din JSON.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#onRemoveClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#onRemoveClick, true);
  }

  /**
   * Click pe cosul de gunoi: rezolvam randul direct din DOM (data-key), nu din index.
   */
  #onRemoveClick = (event) => {
    const btn = event.target instanceof Element ? event.target.closest('.cart-items__remove') : null;
    if (!btn || !this.contains(btn)) return;
    const row = btn.closest('.cart-items__table-row');
    if (!row || !row.dataset.key) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#removeRow(row, null);
  };

  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    if (!line && line !== 0) return;

    const row =
      (event.target instanceof Element ? event.target.closest('.cart-items__table-row') : null) ||
      this.refs.cartItemRows?.[line - 1] ||
      null;

    if (quantity === 0) {
      return this.#removeRow(row, line);
    }

    this.#changeQuantity(row, line, quantity, 'change');

    const textComponent = /** @type {TextComponent | undefined} */ (row?.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Pastrat pentru apelurile temei de tip on:click="/onLineItemRemove/N".
   */
  onLineItemRemove(line) {
    const row = this.refs.cartItemRows?.[line - 1] || null;
    this.#removeRow(row, line);
  }

  #removeRow(row, line) {
    this.#changeQuantity(row, line, 0, 'clear');

    const cartItemRowToRemove = row || this.refs.cartItemRows?.[line - 1];

    if (!cartItemRowToRemove) return;

    const allRows = [...this.querySelectorAll('.cart-items__table-row[data-key]')];

    const rowsToRemove = [
      cartItemRowToRemove,
      ...allRows.filter((r) => r !== cartItemRowToRemove && r.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    const isEmptyCart = rowsToRemove.length >= allRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    rowsToRemove.forEach((r) => {
      const remove = () => r.remove();

      if (prefersReducedMotion()) return remove();

      r.style.setProperty('--row-height', `${r.clientHeight}px`);
      r.classList.add('removing');

      onAnimationEnd(r, remove);
    });
  }

  /**
   * API compatibil cu tema, deleaga catre logica pe key.
   */
  updateQuantity(config) {
    const row = this.refs.cartItemRows?.[config.line - 1] || null;
    this.#changeQuantity(row, config.line, config.quantity, config.action || 'change');
  }

  #changeQuantity(row, line, quantity, action) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const lineKey = row?.dataset?.key;

    const payload = { quantity: quantity };
    if (lineKey) {
      payload.id = lineKey;
    } else if (line) {
      payload.line = line;
    }

    const body = JSON.stringify(payload);

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const cart = JSON.parse(responseText);

        resetShimmer(this);

        if (cart.errors) {
          this.#handleCartError(row, line, cart);
          return;
        }

        this.#updateQuantitySelectors(cart);
        this.#fastRefresh(cart);
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  #fastRefresh(cart) {
    const fmt = (cents) =>
      new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) +
      ' lei';

    const parseMoney = (s) => {
      if (!s) return 0;
      const m = String(s)
        .replace(/[^\d,.]/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
      const v = parseFloat(m);
      return isNaN(v) ? 0 : Math.round(v * 100);
    };

    const byKey = new Map((cart.items || []).map((i) => [i.key, i]));
    let saved = cart.total_discount || 0;

    [...this.querySelectorAll('.cart-items__table-row[data-key]')].forEach((row) => {
      if (!row.isConnected) return;
      const it = byKey.get(row.dataset.key);
      if (!it) {
        if (!row.classList.contains('removing')) row.remove();
        return;
      }
      const priceEl = row.querySelector('.cart-items__price text-component');
      if (priceEl) {
        priceEl.textContent = fmt(it.final_line_price);
        priceEl.setAttribute('value', fmt(it.final_line_price));
      }
      const input = row.querySelector('input[name="updates[]"], .quantity-selector input');
      if (input) input.value = it.quantity;

      const cmpEl = row.querySelector('s.compare-at-price');
      if (cmpEl) {
        const cmpUnit = parseMoney(cmpEl.textContent);
        if (cmpUnit > it.final_price) {
          saved += (cmpUnit - it.final_price) * it.quantity;
        }
      }
    });

    document.querySelectorAll('.cart-totals__total text-component, [ref="cartTotal"]').forEach((el) => {
      el.textContent = fmt(cart.total_price);
      el.setAttribute('value', fmt(cart.total_price));
    });

    document.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count').forEach((el) => {
      el.textContent = cart.item_count;
    });

    const total = cart.total_price;
    const hasGift = (cart.items || []).some((i) => i.final_price === 0);
    document.querySelectorAll('.gift-progress').forEach((bar) => {
      const fill = bar.querySelector('.gift-progress__fill');
      if (fill) fill.style.width = Math.min(100, total / 1000) + '%';
      const txt = bar.querySelector('.gift-progress__text');
      if (!txt) return;
      if (total < 20000) {
        txt.innerHTML =
          'Mai adaugă <strong>' +
          fmt(20000 - total) +
          '</strong> pentru <strong>livrare GRATUITĂ</strong> <span class="gift-progress__icon">🚚</span>';
      } else if (total < 100000) {
        txt.innerHTML =
          '<span class="gift-progress__ok">✓ Livrare gratuită</span> · Mai adaugă <strong>' +
          fmt(100000 - total) +
          '</strong> și alegi un <strong>cadou gratuit</strong> <span class="gift-progress__icon">🎁</span>';
      } else if (hasGift) {
        txt.innerHTML = '<span class="gift-progress__icon">🎉</span> <strong>Cadoul ți-a fost aplicat automat!</strong>';
      } else {
        txt.innerHTML =
          '<span class="gift-progress__icon">🎁</span> <strong>Ai deblocat cadoul GRATUIT!</strong> <a href="/collections/cadouri-gratuite" class="gift-progress__link">Alege →</a>';
      }
    });

    document.querySelectorAll('.cart-savings, .xs-sv-clone').forEach((el) => {
      if (saved > 0) {
        el.innerHTML = 'Economisești <strong>' + fmt(saved) + '</strong> dacă comanzi acum 🎉';
        if (el.classList.contains('xs-sv-clone')) el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    // Cos gol dupa update -> template-ul de cos gol
    if (cart.item_count === 0) {
      const template = document.getElementById('empty-cart-template');
      if (template instanceof HTMLTemplateElement) {
        const clone = document.importNode(template.content, true);
        this.replaceChildren(clone);
      }
    }

    setTimeout(() => document.dispatchEvent(new CustomEvent('cart:refresh')), 350);
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (row, line, parsedResponseText) => {
    const quantityInput =
      row?.querySelector('.quantity-selector input, input[name="updates[]"]') ||
      this.refs.quantitySelectors?.[line - 1]?.querySelector('input');

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const errText = row?.querySelector('.cart-item__error-text');
    const errContainer = row?.querySelector('.cart-items__error');
    if (errText && errContainer) {
      errText.textContent = parsedResponseText.errors;
      errContainer.classList.remove('hidden');
    }
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, 'full', { injectStylesheet: true });

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
